import { useRef, type ReactNode } from "react";
import { LoaderCircle, ShieldCheck, UserRound } from "lucide-react";
import { CREDENTIAL_FORM_DESCRIPTORS } from "@api/shared-runtime/credential-form-descriptors";
import { CredentialType } from "@api/shared-runtime/enums";
import { Button } from "@/components/ui/button";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CredentialFormFieldInput } from "@/features/credentials/components/credential-edit-modal/credential-form-field";
import type {
  Credential,
  CredentialDocument,
  CredentialVerification,
} from "@/features/credentials/types";
import { PersonalInfoForm } from "./personal-info-form";
import { useCredentialVaultEditor } from "./use-credential-vault-editor";
import { ProviderCredentialDocuments } from "./provider-credential-documents";
import { CredentialVerifications } from "../credential-card/credential-verifications";
import { ProviderCredentialUpload } from "./provider-credential-upload";
import { ProviderCredentialConfirmationDialogs } from "./provider-credential-confirmation-dialogs";
import type { PreviewDocument } from "@/features/credentials/hooks/use-credential-document-url";
import {
  vaultSubjectUserId,
  type CredentialVaultScope,
} from "../../lib/credential-vault-scope";
import { credentialDisplayLabel } from "@/features/credentials/lib/credential-display-label";
import { ProviderProfessionalDetailsForm } from "./provider-professional-details-form";
import { ProviderBoardSpecialtyField } from "./provider-board-specialty-field";
import {
  hasProfessionalProfile,
  npiSelectionPatch,
  professionalDetailsPatch,
  professionalDetailsValue,
} from "./provider-professional-form-values";

export function CredentialVaultEditModal({
  credential,
  relatedCredentials,
  scope,
  onClose,
  onOpenDocument,
  onOpenVerificationEvidence,
  onOpenReviewDocument,
  reviewDocumentId,
  onSaved,
  children,
}: {
  credential: Credential;
  relatedCredentials?: Credential[];
  scope: CredentialVaultScope;
  onClose: () => void;
  onOpenDocument?: (document: CredentialDocument) => void;
  onOpenVerificationEvidence?: (verification: CredentialVerification) => void;
  onOpenReviewDocument?: (document: PreviewDocument) => void;
  reviewDocumentId?: string | null;
  onSaved?: () => void;
  children?: ReactNode;
}) {
  const userId = vaultSubjectUserId(scope);
  const openerRef = useRef<HTMLElement | null>(null);
  const credentialType = credential.credentialType as CredentialType;
  const descriptor = CREDENTIAL_FORM_DESCRIPTORS[credentialType];
  const isPersonalInfo = credentialType === CredentialType.PersonalInfo;
  const isNpi = credentialType === CredentialType.Npi;
  const isBoardCertification =
    credentialType === CredentialType.BoardCertification;
  const HeaderIcon = isPersonalInfo ? UserRound : ShieldCheck;
  const editor = useCredentialVaultEditor({
    credential,
    relatedCredentials,
    scope,
    reviewDocumentId,
    onClose,
    onSaved,
  });
  const {
    values,
    baseline,
    formError,
    isPending,
    isEditing,
    requestClose,
    save,
    changeValue,
    changeValues,
    setShowRemoveWarning,
  } = editor;
  const displayLabel = isPersonalInfo
    ? "personal details"
    : credentialDisplayLabel({
        credential,
        draftCertKind: values["details.certKind"],
      });
  let headingContext = "Credential vault";
  if (isPersonalInfo) {
    headingContext =
      scope.kind === "organization" ? "Professional details" : "Your profile";
  } else if (displayLabel !== credential.label) {
    headingContext = credential.label;
  }
  const hasDocuments =
    credential.documents.length > 0 || editor.review.selectedIds.size > 0;
  const verifications = credential.verifications ?? [];
  const hasEvidence = hasDocuments || verifications.length > 0;
  const showEvidenceColumns = !isPersonalInfo && hasEvidence;
  const isProcessingFiles = editor.isUploadingFiles || editor.isParsingFiles;
  let saveLabel = "Save";
  if (isPending) {
    saveLabel = "Saving…";
  } else if (editor.isUploadingFiles) {
    saveLabel = "Uploading…";
  } else if (editor.isParsingFiles) {
    saveLabel = "Parsing…";
  }
  const professionalSection = hasProfessionalProfile(credentialType) ? (
    <ProviderProfessionalDetailsForm
      value={professionalDetailsValue({ credentialType, values })}
      disabled={editor.isFieldsDisabled}
      allowNoNpi={isPersonalInfo}
      onChange={(patch) =>
        changeValues(professionalDetailsPatch({ credentialType, patch }))
      }
      onNpiSelect={(selection) =>
        changeValues(npiSelectionPatch({ credentialType, selection }))
      }
    />
  ) : undefined;
  const visibleFields = descriptor.fields.filter((field) => {
    if (
      isNpi &&
      ["column.number", "details.taxonomyCodes"].includes(
        `${field.source}.${field.key}`,
      )
    ) {
      return false;
    }
    return !(isBoardCertification && field.key === "specialtyNuccCode");
  });

  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent
        className={cn(
          PROFILE_FORM_STYLES.dialog,
          showEvidenceColumns && "lg:[&.dialog-content]:max-w-6xl",
        )}
        onOpenAutoFocus={() => {
          if (document.activeElement instanceof HTMLElement) {
            openerRef.current = document.activeElement;
          }
        }}
        onCloseAutoFocus={(event) => {
          // The editor can open at the same time as the vault, so its opener
          // may be a profile action outside the sheet's focus trap.
          const parentDialog = Array.from(
            document.querySelectorAll<HTMLElement>(
              '[role="dialog"][data-state="open"]',
            ),
          ).at(-1);
          if (!parentDialog) {
            return;
          }
          event.preventDefault();
          if (
            openerRef.current?.isConnected &&
            parentDialog.contains(openerRef.current)
          ) {
            openerRef.current.focus();
          } else {
            parentDialog.focus();
          }
        }}
      >
        <DialogHeader className={PROFILE_FORM_STYLES.header}>
          <div className="flex items-start gap-4">
            <div className={PROFILE_FORM_STYLES.icon}>
              <HeaderIcon aria-hidden="true" className="size-5" />
            </div>
            <div className="min-w-0 space-y-1.5">
              <p className={PROFILE_FORM_STYLES.eyebrow}>{headingContext}</p>
              <DialogTitle className={PROFILE_FORM_STYLES.title}>
                {isEditing ? "Edit" : "Add"} {displayLabel}
              </DialogTitle>
              <DialogDescription className={PROFILE_FORM_STYLES.description}>
                {isPersonalInfo
                  ? "Keep personal and professional details up to date."
                  : "Manage the details and documents for this credential."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          {isProcessingFiles && (
            <div
              role="status"
              aria-live="polite"
              className="flex shrink-0 items-start gap-3 border-y border-primary/20 bg-primary/5 px-6 py-4 sm:px-8"
            >
              <LoaderCircle
                className="mt-0.5 size-5 shrink-0 animate-spin text-primary"
                aria-hidden="true"
              />
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold text-primary">
                  {editor.isUploadingFiles
                    ? "Uploading your file…"
                    : "Parsing credential details…"}
                </p>
                <p className="text-sm leading-relaxed text-primary">
                  Credential fields are temporarily disabled while we upload
                  your file and extract its details. You can review and correct
                  the results before saving.
                </p>
              </div>
            </div>
          )}
          <div className={PROFILE_FORM_STYLES.body}>
            <ProviderCredentialUpload
              credential={credential}
              userId={userId}
              expectedAffiliationId={
                scope.kind === "organization"
                  ? scope.expectedAffiliationId
                  : undefined
              }
              organizationBusinessUnitId={
                scope.kind === "organization"
                  ? scope.organizationBusinessUnitId
                  : undefined
              }
              review={editor.review}
              disabled={isPending}
              isEditing={isEditing}
            />
            <div
              className={cn(
                "space-y-6",
                showEvidenceColumns &&
                  "lg:grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start lg:gap-6 lg:space-y-0",
              )}
            >
              {hasEvidence && (
                <div
                  className={cn(
                    "space-y-6",
                    showEvidenceColumns && "lg:sticky lg:top-0",
                  )}
                >
                  <CredentialVerifications
                    verifications={verifications}
                    onOpenEvidence={onOpenVerificationEvidence}
                  />
                  {hasDocuments && (
                    <ProviderCredentialDocuments
                      credential={credential}
                      scope={scope}
                      review={editor.review}
                      disabled={isPending}
                      onOpenDocument={onOpenDocument}
                      onOpenReviewDocument={onOpenReviewDocument}
                    />
                  )}
                </div>
              )}
              <div className="min-w-0 space-y-6">
                {!isPersonalInfo && (
                  <p className="text-sm leading-relaxed text-slate-600">
                    {editor.review.selectedIds.size > 0
                      ? "Review the details below and make any corrections before saving."
                      : "Prefer to enter details manually? You can edit the fields below without uploading a file."}
                  </p>
                )}
                {isPersonalInfo && (
                  <PersonalInfoForm
                    values={values}
                    baseline={isEditing ? baseline : undefined}
                    disabled={editor.isFieldsDisabled}
                    onChange={changeValue}
                    professionalSection={professionalSection}
                  />
                )}
                {isNpi && professionalSection}
                {visibleFields.length > 0 && (
                  <section className={PROFILE_FORM_STYLES.section}>
                    <h3 className={PROFILE_FORM_STYLES.sectionHeading}>
                      {isPersonalInfo
                        ? "Additional details"
                        : "Credential details"}
                    </h3>
                    <div className="grid gap-5 sm:grid-cols-2">
                      {visibleFields.map((field) => {
                        const key = `${field.source}.${field.key}`;
                        // Filling a legacy DEA number completes its identity;
                        // keep it editable until this draft has been saved.
                        const canFillDeaNumber =
                          scope.kind === "organization" &&
                          credentialType === CredentialType.Dea &&
                          key === "column.number" &&
                          !String(baseline[key] ?? "").trim();
                        const isLockedIdentity =
                          isEditing &&
                          field.isIdentity === true &&
                          !canFillDeaNumber;
                        if (isBoardCertification && field.key === "specialty") {
                          return (
                            <ProviderBoardSpecialtyField
                              key={key}
                              specialty={String(values[key] ?? "")}
                              taxonomyCode={String(
                                values["details.specialtyNuccCode"] ?? "",
                              )}
                              disabled={editor.isFieldsDisabled}
                              isEditing={isEditing}
                              onChange={({ specialty, taxonomyCode }) =>
                                changeValues({
                                  [key]: specialty,
                                  "details.specialtyNuccCode": taxonomyCode,
                                })
                              }
                            />
                          );
                        }
                        return (
                          <div
                            key={key}
                            className={cn(
                              "min-w-0",
                              (field.kind === "textarea" ||
                                field.kind === "string-list") &&
                                "sm:col-span-2",
                            )}
                          >
                            <CredentialFormFieldInput
                              field={
                                isEditing && !canFillDeaNumber
                                  ? field
                                  : { ...field, isIdentity: false }
                              }
                              appearance="profile"
                              value={values[key] ?? ""}
                              disabled={
                                editor.isFieldsDisabled || isLockedIdentity
                              }
                              changedFrom={
                                isEditing && values[key] !== baseline[key]
                                  ? baseline[key]
                                  : undefined
                              }
                              onRevert={() =>
                                changeValue(key, baseline[key] ?? "")
                              }
                              onChange={(value) => changeValue(key, value)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
              </div>
            </div>
            {formError && (
              <p role="alert" className={PROFILE_FORM_STYLES.error}>
                {formError}
              </p>
            )}
          </div>
          <DialogFooter className={PROFILE_FORM_STYLES.footer}>
            {isEditing && (
              <Button
                type="button"
                variant="ghost"
                className="h-11 rounded-xl px-3 text-xs font-semibold text-red-600 hover:bg-red-50 hover:text-red-700 sm:mr-auto"
                disabled={isPending || editor.hasPendingUpload}
                onClick={() => setShowRemoveWarning(true)}
              >
                Remove credential
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className={PROFILE_FORM_STYLES.secondaryButton}
              disabled={isPending || editor.review.uploadState.isUploading}
              onClick={requestClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className={PROFILE_FORM_STYLES.primaryButton}
              disabled={!editor.canSave}
            >
              {(isPending || isProcessingFiles) && (
                <LoaderCircle
                  className="mr-2 size-4 animate-spin"
                  aria-hidden="true"
                />
              )}
              {saveLabel}
            </Button>
          </DialogFooter>
        </form>

        <ProviderCredentialConfirmationDialogs
          editor={editor}
          credentialLabel={displayLabel}
          onDiscard={onClose}
        />
        {children}
      </DialogContent>
    </Dialog>
  );
}
